import {
  StatusFilterValue,
  TApiApplicationDetails,
  TApiApplicationDocument,
  TApiSubmitApplicationResponse,
  TApplicationStatus,
  TDocumentStatus,
  TLocalFileEntry,
  TLocalFiles,
  TSignatureValidationState,
} from '~/modules/covenants/types.ts';
import {
  type IStatusConfig,
  buildApplicationDocumentRowKey,
  findApplicationDocumentByPreviewId,
  formatDate,
  getSignatureValidationMessages,
} from '~/modules/covenants/utils.ts';
import { getApplicationDocumentStatusConfig } from '~/modules/covenants/statusConfig.ts';
import { COVENANTS_DATE_TIME_FORMAT } from '~/modules/covenants/constants.tsx';
import {
  APPLICATION_DOCUMENT_TABLE_HEADER_IDS,
  APPLICATION_DOCUMENT_TABLE_HEADERS,
  APPLICATION_FORM_LABELS,
  DOC_TABLE_ROW_LABELS,
} from '~/modules/covenants/CovenantsDataApplicationForm/constants.ts';

export type TGroupedDocumentRowStatus = TDocumentStatus | StatusFilterValue.PartlyAccepted;
export type TDocRowDividerVariant = 'none' | 'full' | 'inset';

export const getApplicationFormTitle = ({
  id,
  localId,
}: Pick<TApiApplicationDetails, 'id' | 'localId'>) =>
  localId != null
    ? `${APPLICATION_FORM_LABELS.applicationTitleNumberPrefix}${localId}`
    : `${APPLICATION_FORM_LABELS.applicationTitlePrefix} ${id}`;

export const getApplicationFormCreatedAtLabel = (createdAt?: string) =>
  createdAt
    ? `${APPLICATION_FORM_LABELS.createdAtPrefix} ${formatDate(
        createdAt,
        COVENANTS_DATE_TIME_FORMAT
      )}`
    : null;

export const getApplicationDocumentTableHeaders = (showDeadlineColumn: boolean) =>
  showDeadlineColumn
    ? APPLICATION_DOCUMENT_TABLE_HEADERS
    : APPLICATION_DOCUMENT_TABLE_HEADERS.filter(
        (header) => header.id !== APPLICATION_DOCUMENT_TABLE_HEADER_IDS.deadline
      );

export const getParentRowDividerVariant = ({
  isExpanded,
  fileRowsCount,
  isLastGroup,
}: {
  isExpanded: boolean;
  fileRowsCount: number;
  isLastGroup: boolean;
}): TDocRowDividerVariant => {
  if (isExpanded && fileRowsCount > 0) {
    return 'inset';
  }

  return isLastGroup ? 'none' : 'full';
};

export const getFileRowDividerVariant = ({
  fileIndex,
  fileRowsCount,
  isLastGroup,
}: {
  fileIndex: number;
  fileRowsCount: number;
  isLastGroup: boolean;
}): TDocRowDividerVariant => {
  if (fileIndex < fileRowsCount - 1) {
    return 'inset';
  }

  return isLastGroup ? 'none' : 'full';
};

export const getApplicationDocumentPeriodLabel = ({
  childPeriod,
  yearPeriod,
}: Pick<TApiApplicationDocument, 'childPeriod' | 'yearPeriod'>) => {
  if (childPeriod && yearPeriod) {
    return `${childPeriod} / ${yearPeriod}`;
  }

  return childPeriod || yearPeriod || DOC_TABLE_ROW_LABELS.emptyValue;
};

export const getApplicationDocumentDeadlineLabel = (deadline?: string) =>
  deadline ? formatDate(deadline) : DOC_TABLE_ROW_LABELS.emptyValue;

export const hasAnyApplicationFiles = (
  documents: TApiApplicationDocument[],
  localFiles: TLocalFiles
): boolean => {
  return documents.some((document) => {
    const localFile = localFiles[buildApplicationDocumentRowKey(document)];

    return !!document.fileName || !!localFile?.file;
  });
};

export const hasExternalSignatureForDocument = (
  document: TApiApplicationDocument,
  localFiles: TLocalFiles,
  signatureValidationState: TSignatureValidationState
): boolean => {
  const localFile = localFiles[buildApplicationDocumentRowKey(document)];
  const documentSignatureValidationState = signatureValidationState[String(document.documentId)];

  return (
    !!localFile?.signature ||
    !!localFile?.isExternal ||
    document.isCustomSignFile ||
    !!document.hasSignedFile ||
    !!documentSignatureValidationState?.hasGeneratedProtocol
  );
};

export const buildSignatureValidationContent = (response: TApiSubmitApplicationResponse): string => {
  const messages = getSignatureValidationMessages(response);

  return [
    'Під час перевірки підписів виявлено помилки:',
    ...messages.map((message) => `• ${message}`),
  ].join('\n');
};

export const getMaxDocumentsPage = (totalElements: number, rowsPerPage: number): number => {
  return Math.max(0, Math.ceil(totalElements / rowsPerPage) - 1);
};

export const clearSignatureValidationStateForDocument = (
  signatureValidationState: TSignatureValidationState,
  document: TApiApplicationDocument
): TSignatureValidationState => {
  const documentKeys = [document.documentId, document.id].filter((key): key is string => !!key);

  if (!documentKeys.length) {
    return signatureValidationState;
  }

  let hasChanges = false;
  const nextState = { ...signatureValidationState };

  documentKeys.forEach((key) => {
    const normalizedKey = String(key);

    if (normalizedKey in nextState) {
      delete nextState[normalizedKey];
      hasChanges = true;
    }
  });

  return hasChanges ? nextState : signatureValidationState;
};

export const getDocumentPreviewPayload = (
  documents: TApiApplicationDocument[],
  previewId: string | number,
  localFiles: TLocalFiles
): { document: TApiApplicationDocument; localFile?: TLocalFileEntry } | null => {
  const document = findApplicationDocumentByPreviewId(documents, previewId);

  if (!document) {
    return null;
  }

  const rowKey = buildApplicationDocumentRowKey(document);

  return {
    document,
    localFile: localFiles[rowKey],
  };
};

export const deriveGroupedDocumentStatus = (
  documents: TApiApplicationDocument[]
): TGroupedDocumentRowStatus | null => {
  if (!documents.length) {
    return null;
  }

  const uniqueStatuses = Array.from(new Set(documents.map((document) => document.status)));

  if (uniqueStatuses.length === 1) {
    return uniqueStatuses[0] as TDocumentStatus;
  }

  return StatusFilterValue.PartlyAccepted;
};

export const shouldShowGroupedDocumentStatus = (
  applicationStatus: TApplicationStatus,
  isExpanded: boolean
): boolean => applicationStatus !== StatusFilterValue.New && !isExpanded;

export const getGroupedDocumentStatusConfig = (
  status: TGroupedDocumentRowStatus
): IStatusConfig => {
  if (status === StatusFilterValue.PartlyAccepted) {
    return {
      label: 'Доопрацювання',
      color: 'warning',
      description:
        'У межах цього документа є вкладені файли з різними статусами, тому документ потребує доопрацювання.',
    };
  }

  return getApplicationDocumentStatusConfig(status);
};
